"use client"

import React, { useState } from 'react'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'
import { UITypography } from '../display/ui-typography'
import { UICollapsible, UICollapsibleContent, UICollapsibleTrigger } from '../layout/ui-collapsible'
import { ChevronDown, Brain, Loader2 } from 'lucide-react'

export interface UIReasoningStep {
  id: string
  content: string
  timestamp?: Date
  status?: 'pending' | 'processing' | 'completed'
}

export interface UIReasoningProps {
  // 핵심 데이터
  steps?: UIReasoningStep[]
  content?: string

  // 상태
  isStreaming?: boolean
  isCollapsed?: boolean
  defaultCollapsed?: boolean
  onCollapsedChange?: (collapsed: boolean) => void

  // UI 옵션
  variant?: 'default' | 'inline'
  showIcon?: boolean
  showTimestamp?: boolean
  autoCollapse?: boolean

  // 스타일링
  className?: string

  // 접근성 및 i18n
  ariaLabel?: string
  locale?: 'ko' | 'en'
}

/**
 * UIReasoning - AI의 사고 과정을 표시하는 컴포넌트
 * shadcn.io/ai Reasoning 컴포넌트 기반으로 구현
 */
export const UIReasoning = React.forwardRef<HTMLDivElement, UIReasoningProps>(
  ({
    steps = [],
    content,
    isStreaming = false,
    isCollapsed: controlledCollapsed,
    defaultCollapsed = true,
    onCollapsedChange,
    variant = 'default',
    showIcon = true,
    showTimestamp = false,
    autoCollapse = true,
    className,
    ariaLabel,
    locale = 'ko',
    ...props
  }, ref) => {
    const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed)
    const [isPressed, setIsPressed] = useState(false)

    const isControlled = controlledCollapsed !== undefined
    const collapsed = isControlled ? controlledCollapsed : internalCollapsed

    const handleCollapsedChange = (value: boolean) => {
      if (!isControlled) {
        setInternalCollapsed(value)
      }
      onCollapsedChange?.(value)
    }

    // 스트리밍이 완료되면 자동으로 접기
    React.useEffect(() => {
      if (autoCollapse && !isStreaming && steps.length > 0) {
        const timer = setTimeout(() => {
          handleCollapsedChange(true)
        }, 2000)
        return () => clearTimeout(timer)
      }
    }, [isStreaming, autoCollapse, steps.length])

    const hasContent = content || steps.length > 0

    if (!hasContent) return null

    return (
      <UIBox
        ref={ref}
        role="region"
        className={cn(
          // Orange-infused Apple glassmorphism - 완결성 있는 오렌지 톤
          "rounded-xl transition-all duration-300 ease-out",
          // 기본 오렌지 톤 배경 (매우 미묘하게)
          "bg-gradient-to-br from-orange-50/[0.03] to-amber-50/[0.02] border shadow-xl",
          "dark:from-orange-950/[0.15] dark:to-amber-950/[0.10]",
          // 오렌지 accent 테두리
          "border-orange-200/[0.15] dark:border-orange-400/[0.20]",
          // 오렌지 glow 그림자
          "shadow-orange-500/[0.08] dark:shadow-orange-400/[0.25]",
          // Enhanced expanded state - 더 강한 오렌지 효과
          !collapsed && [
            "from-orange-50/[0.06] to-amber-50/[0.04] border-orange-200/[0.25]",
            "dark:from-orange-950/[0.25] dark:to-amber-950/[0.20] dark:border-orange-400/[0.30]",
            "shadow-2xl shadow-orange-500/[0.12] dark:shadow-orange-400/[0.35]",
            "dark:shadow-orange-300/[0.08]" // Inner orange glow
          ],
          // 세련된 호버 효과 - 오렌지 강화
          "hover:from-orange-50/[0.05] hover:to-amber-50/[0.03] hover:border-orange-200/[0.20]",
          "dark:hover:from-orange-950/[0.20] dark:hover:to-amber-950/[0.15] dark:hover:border-orange-400/[0.25]",
          "hover:shadow-xl hover:shadow-orange-500/[0.10] dark:hover:shadow-orange-400/[0.30]",
          "hover:-translate-y-0.5 hover:scale-[1.005]",
          variant === 'inline' && "inline-block",
          className
        )}
        aria-label={ariaLabel || (locale === 'ko' ? 'AI 추론 과정' : 'AI reasoning process')}
        {...props}
      >
        <UICollapsible
          open={!collapsed}
          onOpenChange={(open) => handleCollapsedChange(!open)}
        >
          <UICollapsibleTrigger asChild>
            <button
              className={cn(
                "w-full flex items-center justify-between px-4 py-3 text-sm transition-all duration-300 ease-out",
                "rounded-lg focus:outline-none",
                // Apple-style hover: subtle brightness increase
                "hover:brightness-105 hover:bg-white/[0.015] dark:hover:bg-white/[0.01]",
                // 개선된 클릭 상태 - 자연스러운 오렌지 강조
                isPressed && "bg-accent/[0.08] border-accent/20 shadow-sm",
                // Subtle active scale
                "active:scale-[0.99] active:transition-transform active:duration-150"
              )}
              onMouseDown={() => {
                setIsPressed(true)
                // 자연스러운 타이밍으로 pressed 상태 해제
                setTimeout(() => {
                  setIsPressed(false)
                }, 200)
              }}
              onMouseUp={() => {
                // 마우스업 시 포커스 제거 (키보드 접근성은 유지)
                setTimeout(() => {
                  const target = document.activeElement as HTMLElement
                  if (target && target.tagName === 'BUTTON') {
                    target.blur()
                  }
                }, 100)
              }}
            >
              <UIBox className="flex items-center gap-2">
                {showIcon && (
                  <Brain className={cn(
                    "h-4 w-4 transition-all duration-300 ease-out",
                    // 오렌지 톤 아이콘 색상
                    collapsed
                      ? "text-orange-600/80 dark:text-orange-400/90"
                      : "text-orange-500/70 dark:text-orange-400/80",
                    // Enhanced icon effects with orange glow
                    "drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(251,146,60,0.3)]",
                    isStreaming && "animate-pulse"
                  )} />
                )}
                <UITypography
                  variant="caption"
                  weight="medium"
                  className={cn(
                    "transition-colors duration-300 ease-out",
                    // 오렌지 배경에 최적화된 텍스트 색상
                    collapsed
                      ? "text-orange-900/90 dark:text-orange-100/95"
                      : "text-orange-800/80 dark:text-orange-100/85",
                    // Enhanced typography with orange-optimized shadow
                    "drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]"
                  )}
                >
                  {locale === 'ko' ? '생각하는 중' : 'Thinking'}
                </UITypography>
                {isStreaming && (
                  <UIBox className="relative">
                    <Loader2 className={cn(
                      "h-3 w-3 animate-spin transition-colors duration-300 ease-out",
                      // 오렌지 스피너 색상
                      collapsed
                        ? "text-orange-600/80 dark:text-orange-400/90"
                        : "text-orange-500/70 dark:text-orange-400/80",
                      // Enhanced orange spinner effects
                      "drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(251,146,60,0.4)]"
                    )} />
                    {/* 오렌지 glow effect */}
                    <UIBox className={cn(
                      "absolute inset-0 rounded-full animate-pulse transition-opacity duration-300",
                      "bg-orange-400/15 dark:bg-orange-400/25 blur-sm",
                      collapsed ? "opacity-70" : "opacity-40"
                    )} />
                  </UIBox>
                )}
              </UIBox>
              <ChevronDown className={cn(
                "h-4 w-4 transition-all duration-300 ease-out",
                // 오렌지 톤 chevron
                "text-orange-500/60 dark:text-orange-400/70",
                "drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(251,146,60,0.3)]",
                !collapsed && "rotate-180",
                // 오렌지 호버 효과
                "group-hover:text-orange-600/80 dark:group-hover:text-orange-300/90"
              )} />
            </button>
          </UICollapsibleTrigger>

          <UICollapsibleContent className="transition-all duration-300 ease-out data-[state=closed]:animate-out data-[state=closed]:fade-out-50 data-[state=open]:animate-in data-[state=open]:fade-in-50">
            <UIBox className="px-4 pb-4">
              {content ? (
                <UITypography
                  variant="body2"
                  className={cn(
                    "whitespace-pre-wrap transition-colors duration-300 ease-out leading-relaxed",
                    // 오렌지 배경에 최적화된 본문 텍스트
                    "text-orange-950/90 dark:text-orange-50/95",
                    // Enhanced readability with better contrast
                    "drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]"
                  )}
                >
                  {content}
                  {isStreaming && (
                    <span className={cn(
                      "inline-block w-1 h-3 animate-pulse ml-1 rounded-full transition-all duration-300 ease-out",
                      // 오렌지 커서
                      collapsed
                        ? "bg-orange-600/60 dark:bg-orange-400/80"
                        : "bg-orange-500/50 dark:bg-orange-400/70",
                      // Enhanced orange cursor effects
                      "shadow-sm dark:shadow-orange-400/30"
                    )} />
                  )}
                </UITypography>
              ) : (
                <UIBox className="space-y-2">
                  {steps.map((step) => (
                    <UIBox key={step.id} className="flex items-start gap-2">
                      {step.status === 'processing' && (
                        <Loader2 className="h-3 w-3 mt-1 animate-spin text-foreground/50" />
                      )}
                      {step.status === 'completed' && (
                        <UIBox className="h-3 w-3 mt-1 rounded-full bg-green-500/20 border border-green-500/30 shadow-sm" />
                      )}
                      {step.status === 'pending' && (
                        <UIBox className="h-3 w-3 mt-1 rounded-full bg-foreground/3 border border-foreground/8" />
                      )}
                      <UIBox className="flex-1">
                        <UITypography
                          variant="body2"
                          className={cn(
                            "transition-colors duration-300 ease-out leading-relaxed",
                            // 오렌지 배경에 최적화된 step 텍스트
                            "text-orange-950/85 dark:text-orange-50/90",
                            // Enhanced readability with orange-optimized shadow
                            "drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]"
                          )}
                        >
                          {step.content}
                        </UITypography>
                        {showTimestamp && step.timestamp && (
                          <UITypography
                            variant="caption"
                            className={cn(
                              "transition-colors duration-300 ease-out",
                              // 오렌지 배경에 최적화된 타임스탬프
                              "text-orange-800/70 dark:text-orange-200/80",
                              "drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]"
                            )}
                          >
                            {step.timestamp.toLocaleTimeString()}
                          </UITypography>
                        )}
                      </UIBox>
                    </UIBox>
                  ))}
                </UIBox>
              )}
            </UIBox>
          </UICollapsibleContent>
        </UICollapsible>
      </UIBox>
    )
  }
)

UIReasoning.displayName = "UIReasoning"